import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import config, {
  getAppBuildVersion,
  getPlatform,
  getWebAppVersion,
} from "../../../utils/config";

const WISHLINK_ID_STORAGE_KEY = "sbwlWishlinkId";

/**
 * The SBWL surface (`api/sbwl/...`) rejects any request without a `wishlinkId`
 * header (see sbwl_surface.middlewares.sbwl_master_user_middleware). Seleqt uses
 * a client-generated anonymous id for this; we mirror that here with a persisted
 * value so the creator app resolves to a stable SBWL master user across sessions.
 */
const getOrCreateSbwlWishlinkId = () => {
  try {
    const existing = localStorage.getItem(WISHLINK_ID_STORAGE_KEY);
    if (existing) return existing;
    const generated =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Math.random().toString(36).slice(2)}${Math.random()
            .toString(36)
            .slice(2)}`;
    localStorage.setItem(WISHLINK_ID_STORAGE_KEY, generated);
    return generated;
  } catch (e) {
    return "creator-app";
  }
};

/**
 * Dedicated base query for the SBWL surface. Unlike the sourcing `s/` base query
 * it does NOT forward the creator's Firebase token — the SBWL identity check
 * would 403 on a token whose master user doesn't match the `wishlinkId`. These
 * routes are `AnyUser`, so the anonymous `wishlinkId` is all they need.
 */
const sbwlBaseQuery = fetchBaseQuery({
  baseUrl: config.API.url,
  prepareHeaders: (headers) => {
    headers.set("wishlinkId", getOrCreateSbwlWishlinkId());
    headers.set("x-native-build", String(getAppBuildVersion()));
    headers.set("x-web-app-version", getWebAppVersion() || "0.0.0");
    headers.set("x-platform", getPlatform());
    return headers;
  },
});

export const sourcingBrandReviewsReduxAPI = createApi({
  reducerPath: "sourcingBrandReviewsReduxAPI",
  baseQuery: sbwlBaseQuery,
  endpoints: (builder) => ({
    /** One page of brand reviews. Response: { reviews, total, has_more, page }. */
    getBrandReviews: builder.query({
      query: ({ productId, page = 1, limit = 5 }) => ({
        url: `sbwl/product/${productId}/reviews/?source=brand&page=${page}&limit=${limit}`,
      }),
    }),
  }),
});

export const { useGetBrandReviewsQuery, useLazyGetBrandReviewsQuery } =
  sourcingBrandReviewsReduxAPI;
